import {Language} from 'yougile-lang'
import {Util} from 'yougile-util'
import {rootNodeId} from './constants'
import {DataObjectId} from './data-object'
import {Entity, EntityJSON} from './entity'
import {UserIntegrations} from './integrations'

export type UserUITheme = 'dark' | 'light'
export type UserUIThemeVariation = 'none' | 'auto' | 'darker' | 'lighter'
export type UserUIThemeVariationPerTheme = Record<
  UserUITheme,
  UserUIThemeVariation
>

export interface UserJSON extends EntityJSON {
  language: Language
  companyOrder: DataObjectId[]
  customSkins: string[]
  resources: number
  skin: string
  email: string
  timeDiff: number
  theme?: UserUITheme
  integrations?: UserIntegrations
  // only standalone
  userRole?: string
}

export interface UserPrivileges {
  isAdmin: boolean
}

/**
 * “.deleted” property should not be used.
 * Remove the connection between company Hub and the user instead.
 */
export class User extends Entity {
  public companyOrder: DataObjectId[]
  public skin = '-images/10-04'
  public email = ''
  public language: Language
  public customSkins: string[]
  public timeDiff: number
  public theme: UserUITheme
  public integrations?: UserIntegrations
  // only standalone
  public userRole?: string

  constructor(companyId: DataObjectId, id: DataObjectId, email: string) {
    super(companyId, '')
    this.id = id
    this.email = email
    this.title = this.id
    this.location = rootNodeId
    this.dataType = 'User'
    this.companyOrder = []
    this.timeDiff = 0
    this.theme = 'light'
    this.language = 'en'
    this.customSkins = []
    this.isLazy = false
  }

  public override fromJSON(companyId: DataObjectId, json: UserJSON): User {
    super.fromJSON(companyId, json)
    // redefining title as data id in order to prevent name conflicts
    this.title = this.id
    this.language = json.language || 'en'
    this.skin = json.skin || '7'
    this.email = json.email || ''
    this.companyOrder = Util.clone(json.companyOrder || [])
    this.customSkins = Util.clone(json.customSkins || [])
    this.timeDiff = json.timeDiff || 0
    this.theme = json.theme ?? 'light'
    if (json.integrations) {
      this.integrations = Util.clone(json.integrations)
    }
    this.userRole = json.userRole
    if (this.deleted) {
      // user can not be deleted
      this.deleted = false
    }

    return this
  }

  public override toJSON(): UserJSON {
    const res = super.toJSON() as UserJSON
    res.language = this.language
    res.skin = this.skin
    res.email = this.email
    res.companyOrder = Util.clone(this.companyOrder || [])
    res.customSkins = Util.clone(this.customSkins || [])
    res.timeDiff = this.timeDiff
    res.theme = this.theme === 'dark' ? 'dark' : undefined

    res.integrations = Util.clone(this.integrations || {})

    res.userRole = this.userRole

    return res
  }
}

export const isRoleEmail = (email: string): boolean => {
  if (!email) {
    return false
  }

  return email.endsWith('@role.yougile.com')
}
